/**
 * Public Job Detail & Application Form
 * Allows candidates to view job details and apply
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import * as recruitmentService from '../../services/recruitmentService';

const PublicJobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  
  const color = searchParams.get('color') || '#6366f1';
  const companyName = searchParams.get('name') || 'Our Company';
  const logo = searchParams.get('logo');
  
  const [job, setJob] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Prevent multiple simultaneous loads
  const loadingRef = useRef(false);
  const lastLoadedId = useRef<string | null>(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    linkedIn: '',
    portfolioUrl: '',
    coverLetter: '',
    resumeFile: null as File | null,
  });

  useEffect(() => {
    if (id && id !== lastLoadedId.current) {
      loadJob();
    }
  }, [id]);

  const loadJob = async () => {
    if (!id || loadingRef.current) return; // Prevent concurrent requests
    
    try {
      loadingRef.current = true;
      setLoading(true);
      const jobData = await recruitmentService.getJob(id);
      
      // Verify job is publicly accessible
      if (jobData.status !== 'active' || !jobData.posted_to_career_site) {
        toast.error('This job posting is not currently accepting applications');
        return;
      }
      
      setJob(jobData);
      lastLoadedId.current = id;
    } catch (error) {
      console.error('Failed to load job:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please upload a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setFormData({ ...formData, resumeFile: file });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!formData.resumeFile) {
      toast.error('Please upload your resume/CV');
      return;
    }

    try {
      setSubmitting(true);
      
      // Create candidate
      const candidate = await recruitmentService.createCandidate({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        linkedin_url: formData.linkedIn,
        portfolio_url: formData.portfolioUrl,
      });

      // Create application
      await recruitmentService.createApplication({
        job_id: id!,
        candidate_id: candidate.id!,
        application_source: 'career_site',
        cover_letter: formData.coverLetter,
      });

      // In production, you would upload the resume file here
      // await uploadResume(formData.resumeFile, candidate.id);

      setSubmitted(true);
      toast.success('Application submitted successfully!');
      
      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      console.error('Failed to submit application:', error);
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: color }}></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Job Not Found</h2>
          <p className="text-gray-600">This job posting is no longer available</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
            <svg className="w-8 h-8" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Submitted!</h2>
          <p className="text-lg text-gray-600 mb-6">
            Thank you for applying to {job.title} at {companyName}.
          </p>
          <p className="text-gray-600 mb-8">
            We've received your application and will review it shortly. If your qualifications match our requirements, we'll contact you at <strong>{formData.email}</strong> to schedule an interview.
          </p>
          <button
            onClick={() => window.location.href = '/careers'}
            className="px-6 py-3 rounded-lg text-white font-medium transition-colors"
            style={{ backgroundColor: color }}
          >
            View More Opportunities
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => window.history.back()}
          className="mb-6 text-gray-600 hover:text-gray-900 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Jobs
        </button>

        {/* Job Details */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          {logo && (
            <img src={logo} alt={companyName} className="h-12 w-auto mb-4" />
          )}
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{job.title}</h1>
          
          <div className="flex flex-wrap gap-3 mb-6">
            {job.department && (
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-gray-100 text-gray-700">
                🏢 {job.department}
              </span>
            )}
            {job.location && (
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-gray-100 text-gray-700">
                📍 {job.location}
              </span>
            )}
            {job.remote_type && (
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-blue-100 text-blue-700">
                💻 {job.remote_type}
              </span>
            )}
            {job.employment_type && (
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-green-100 text-green-700">
                ⏰ {job.employment_type}
              </span>
            )}
          </div>

          {(job.salary_min || job.salary_max) && (
            <p className="text-xl font-semibold text-gray-900 mb-6">
              💰 {job.salary_currency || '£'}{job.salary_min?.toLocaleString()} - {job.salary_max?.toLocaleString()}
            </p>
          )}

          <div className="prose max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">About the Role</h2>
            <p className="text-gray-700 mb-6">{job.description}</p>

            {job.responsibilities && job.responsibilities.length > 0 && (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Responsibilities</h3>
                <ul className="list-disc list-inside space-y-2 mb-6">
                  {job.responsibilities.map((resp: string, idx: number) => (
                    <li key={idx} className="text-gray-700">{resp}</li>
                  ))}
                </ul>
              </>
            )}

            {job.requirements && job.requirements.length > 0 && (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Requirements</h3>
                <ul className="list-disc list-inside space-y-2 mb-6">
                  {job.requirements.map((req: string, idx: number) => (
                    <li key={idx} className="text-gray-700">{req}</li>
                  ))}
                </ul>
              </>
            )}

            {job.nice_to_haves && job.nice_to_haves.length > 0 && (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Nice to Have</h3>
                <ul className="list-disc list-inside space-y-2 mb-6">
                  {job.nice_to_haves.map((item: string, idx: number) => (
                    <li key={idx} className="text-gray-700">{item}</li>
                  ))}
                </ul>
              </>
            )}

            {job.benefits && job.benefits.length > 0 && (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Benefits</h3>
                <ul className="list-disc list-inside space-y-2 mb-6">
                  {job.benefits.map((benefit: string, idx: number) => (
                    <li key={idx} className="text-gray-700">{benefit}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* Application Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Apply for this Position</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                placeholder="City, Country"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn Profile
                </label>
                <input
                  type="url"
                  name="linkedIn"
                  placeholder="https://linkedin.com/in/yourprofile"
                  value={formData.linkedIn}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portfolio/Website
                </label>
                <input
                  type="url"
                  name="portfolioUrl"
                  placeholder="https://yourportfolio.com"
                  value={formData.portfolioUrl}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resume/CV * (PDF only, max 10MB)
              </label>
              <input
                type="file"
                accept=".pdf"
                required
                onChange={handleFileChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              />
              {formData.resumeFile && (
                <p className="mt-2 text-sm text-green-600">
                  ✓ {formData.resumeFile.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cover Letter (Optional)
              </label>
              <textarea
                name="coverLetter"
                rows={6}
                placeholder="Tell us why you're a great fit for this role..."
                value={formData.coverLetter}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: color }}
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicJobDetail;
